import React from 'react';
import { Helmet } from 'react-helmet';
import { User, BookOpen, Globe2, AwardIcon } from 'lucide-react';
import settings from '../constants/settings';

const About = () => {
  // Team data
  const team = [
    {
      name: 'Taufiq Januardi',
      position: 'Founder & CEO',
      bio: 'Alumni LIPIA Jakarta dengan pengalaman 7 tahun membantu santri mendapatkan beasiswa internasional.'
    },
    {
      name: 'Fatima Zahra',
      position: 'Head of Academic Counseling',
      bio: 'Lulusan Universitas Al-Azhar Kairo dengan spesialisasi dalam pendampingan persiapan akademik.'
    },
    {
      name: 'Muhammad Zaki',
      position: 'International Relations Officer',
      bio: 'Alumni Medina International University dengan jaringan luas di berbagai universitas Timur Tengah.'
    }
  ];
  
  // Values data
  const values = [
    {
      icon: <BookOpen className="h-12 w-12 text-gold" />,
      title: 'Keilmuan yang Mendalam',
      description: 'Kami percaya pendidikan Islam yang berkualitas membutuhkan pemahaman yang komprehensif dan mendalam.'
    },
    {
      icon: <Globe2 className="h-12 w-12 text-gold" />,
      title: 'Perspektif Global',
      description: 'Mempersiapkan santri untuk menjadi ulama yang memahami konteks global dan mampu berkontribusi di level internasional.'
    },
    {
      icon: <AwardIcon className="h-12 w-12 text-gold" />,
      title: 'Keunggulan Akademik',
      description: 'Mendorong standar akademik tertinggi dengan tetap menjunjung nilai-nilai kepesantrenan.'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Tentang Kami | Pintar Pilih Kuliah</title>
        <meta name="description" content="Mengenal lebih dekat tentang Pintar Pilih Kuliah, visi, misi, dan tim kami dalam membantu alumni pesantren meraih pendidikan internasional." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-dark text-white mt-[90px]">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-serif leading-tight mb-6">
              Tentang Kami
            </h1>
            <p className="text-lg md:text-xl text-light-cream mb-4 font-light leading-relaxed">
              Mendedikasikan diri untuk membantu alumni pesantren dan pelajar Indonesia untuk meraih pendidikan tinggi berkualitas di luar negeri
            </p>
          </div>
        </div>
      </section>

      {/* Vision Mission Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-serif leading-tight mb-6 text-dark-navy">Visi Kami</h2>
              <div className="bg-light-cream/20 p-6 rounded-lg border-l-4 border-gold">
                <p className="text-gray text-lg font-light leading-relaxed">
                  Menjadi platform terdepan yang memudahkan alumni pesantren di Indonesia untuk mengakses peluang pendidikan internasional, sehingga dapat melahirkan generasi ulama yang memiliki wawasan global.
                </p>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-serif leading-tight mb-6 text-dark-navy">Misi Kami</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="h-6 w-6 rounded-full bg-gold flex items-center justify-center text-white mr-3 mt-1">1</span>
                  <p className="text-gray font-light leading-relaxed">Memberikan informasi lengkap dan terpercaya tentang beasiswa internasional untuk bidang studi Islam.</p>
                </li>
                <li className="flex items-start">
                  <span className="h-6 w-6 rounded-full bg-gold flex items-center justify-center text-white mr-3 mt-1">2</span>
                  <p className="text-gray font-light leading-relaxed">Membekali santri dengan persiapan akademik dan non-akademik untuk meningkatkan daya saing di tingkat global.</p>
                </li>
                <li className="flex items-start">
                  <span className="h-6 w-6 rounded-full bg-gold flex items-center justify-center text-white mr-3 mt-1">3</span>
                  <p className="text-gray font-light leading-relaxed">Membangun jaringan alumni pesantren internasional yang dapat memberikan dukungan dan mentoring berkelanjutan.</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-light-cream/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif leading-tight mb-4 text-dark-navy">Nilai-Nilai Kami</h2>
            <p className="text-gray text-lg font-light leading-relaxed">
              Prinsip yang memandu setiap langkah dan keputusan kami
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-all text-center">
                <div className="flex justify-center mb-6">{value.icon}</div>
                <h3 className="text-xl font-serif mb-3 text-dark-navy">{value.title}</h3>
                <p className="text-gray font-light leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif leading-tight mb-4 text-dark-navy">Tim Kami</h2>
            <p className="text-gray text-lg font-light leading-relaxed">
              Bertemu dengan para profesional berpengalaman di balik Pintar Pilih Kuliah
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all text-center">
                <div className="w-24 h-24 bg-cream/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <User className="h-12 w-12 text-gold" />
                </div>
                <h3 className="text-xl font-serif mb-1 text-dark-navy">{member.name}</h3>
                <p className="text-gold font-medium mb-4">{member.position}</p>
                <p className="text-gray font-light leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#f6d39d] text-dark-navy">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif leading-tight mb-8">Siap Kuliah Go International?</h2>
            <p className="text-xl leading-relaxed mb-12 font-light">
              Hunting bareng 8700+ alumni pesantren dan pelajar yang sudah gabung.<br className="hidden md:block"/> Jangan sampai ketinggalan peluang-peluang beasiswa luar negeri.
            </p>
            <a
              href={settings.wa_group_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white hover:bg-dark-navy text-dark-navy hover:text-light-cream rounded-full inline-flex items-center justify-center gap-3 transition-all hover:shadow-lg border-2 border-gold hover:border-light-cream text-lg"
            >
              <svg
                className="w-6 h-6 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              </svg>
              Gabung Grup #PintarPilihKuliah di WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
